import { ReactNode } from "react";
import { Redirect } from "wouter";
import { useAuth } from "@/lib/auth-context";
import { isAdminUser } from "@chefaiq/shared/lib/admin";

const MAIN_APP_URL = import.meta.env.VITE_MAIN_APP_URL || window.location.origin;

interface AdminRouteProps {
  children: ReactNode;
}

/**
 * AdminRoute — wraps any route that requires admin access.
 *
 * Guards:
 *  1. Not authenticated  → redirect to /signin (admin login page)
 *  2. Authenticated but not admin → redirect to main app
 *  3. Authenticated + isAdmin → render children
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Still checking token on mount — render nothing to avoid flicker
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    );
  }

  // Not logged in at all
  if (!isAuthenticated) {
    return <Redirect to="/signin" />;
  }

  // Logged in but not an admin — send them back to the main app
  if (!isAdminUser(user)) {
    window.location.href = MAIN_APP_URL;
    return null;
  }

  return <>{children}</>;
}
